import type { DeepPartial, Styles } from 'klinecharts'

export const lightTheme: DeepPartial<Styles> = {
  grid: {
    show: true,
    horizontal: { show: true, size: 1, color: '#e5e7eb', style: 'dashed', dashedValue: [2, 2] },
    vertical: { show: true, size: 1, color: '#e5e7eb', style: 'dashed', dashedValue: [2, 2] },
  },
  candle: {
    type: 'candle_solid',
    priceMark: {
      show: true,
      high: {
        show: true,
        color: '#374151',
        textSize: 10,
        textFamily: 'inherit',
        textWeight: 'normal',
        textOffset: 5,
      },
      low: {
        show: true,
        color: '#374151',
        textSize: 10,
        textFamily: 'inherit',
        textWeight: 'normal',
        textOffset: 5,
      },
      last: {
        show: true,
        upColor: '#16a34a',
        downColor: '#dc2626',
        noChangeColor: '#6b7280',
        line: { show: true, style: 'dashed', dashedValue: [4, 4], size: 1 },
        text: {
          show: true,
          style: 'fill',
          size: 11,
          paddingLeft: 4,
          paddingTop: 2,
          paddingRight: 4,
          paddingBottom: 2,
          borderStyle: 'solid',
          borderSize: 0,
          borderDashedValue: [2, 2],
          color: '#ffffff',
          // Explicit sans stack (matches the app font). 'inherit' is invalid on
          // a canvas, so klinecharts mis-measures the label box width and leaves
          // slack to the right of the digits — a real family fixes the sizing.
          family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
          weight: 'normal',
          borderRadius: 2,
        },
      },
    },
    bar: {
      upColor: '#16a34a',
      downColor: '#dc2626',
      noChangeColor: '#6b7280',
      upBorderColor: '#16a34a',
      downBorderColor: '#dc2626',
      noChangeBorderColor: '#6b7280',
      upWickColor: '#16a34a',
      downWickColor: '#dc2626',
      noChangeWickColor: '#6b7280',
    },
    tooltip: {
      offsetTop: 4,
      text: { marginLeft: 4 },
      custom: () => [
        { title: 'O: ', value: '{open}' },
        { title: 'H: ', value: '{high}' },
        { title: 'L: ', value: '{low}' },
        { title: 'C: ', value: '{close}' },
      ],
    },
  },
  indicator: {
    lastValueMark: { show: false },
    // Native indicator tooltip is fully suppressed — the React IndicatorLegend
    // renders the data (default) and the hover controls instead.
    tooltip: { showRule: 'none', showName: false, showParams: false },
  },
  xAxis: {
    axisLine: { show: true, color: '#d1d5db', size: 1 },
    tickText: { show: true, color: '#6b7280', size: 12, family: 'inherit', weight: 'normal' },
    tickLine: { show: true, size: 1, length: 3, color: '#d1d5db' },
  },
  yAxis: {
    axisLine: { show: true, color: '#d1d5db', size: 1 },
    tickText: { show: true, color: '#6b7280', size: 12, family: 'inherit', weight: 'normal' },
    tickLine: { show: true, size: 1, length: 3, color: '#d1d5db' },
  },
  crosshair: {
    show: true,
    horizontal: {
      show: true,
      line: { show: true, style: 'dashed', dashedValue: [4, 2], size: 1, color: '#9ca3af' },
      text: {
        show: true,
        style: 'fill',
        color: '#ffffff',
        size: 11,
        // 'inherit' is invalid on a canvas — the whole font string is ignored
        // and the label falls back to the default ~10px, rendering smaller than
        // the last-price label. A real family makes the 11px size take effect.
        family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
        weight: 'normal',
        borderStyle: 'solid',
        borderDashedValue: [2, 2],
        borderSize: 0,
        borderColor: 'transparent',
        borderRadius: 2,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        backgroundColor: '#6b7280',
      },
    },
    vertical: {
      show: true,
      line: { show: true, style: 'dashed', dashedValue: [4, 2], size: 1, color: '#9ca3af' },
      text: {
        show: true,
        style: 'fill',
        color: '#ffffff',
        size: 11,
        // 'inherit' is invalid on a canvas — the whole font string is ignored
        // and the label falls back to the default ~10px, rendering smaller than
        // the last-price label. A real family makes the 11px size take effect.
        family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
        weight: 'normal',
        borderStyle: 'solid',
        borderDashedValue: [2, 2],
        borderSize: 0,
        borderColor: 'transparent',
        borderRadius: 2,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        backgroundColor: '#6b7280',
      },
    },
  },
  separator: {
    size: 1,
    color: '#d1d5db',
    fill: true,
    activeBackgroundColor: 'rgba(22,119,255,0.15)',
  },
  // Selected-overlay axis labels — match the regular tick size/family (see the
  // dark theme note); the blue chip background is kept.
  overlay: {
    text: {
      size: 11,
      family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
      paddingTop: 2,
      paddingBottom: 2,
    },
    rectText: {
      size: 11,
      family: 'IBM Plex Sans, ui-sans-serif, system-ui, sans-serif',
      paddingTop: 2,
      paddingBottom: 2,
    },
  },
} as DeepPartial<Styles>
